// Compact language switcher used in AppShell top bar + Settings.
// Renders a button showing the current language's native name + chevron, opening a
// Popover with all 10 supported languages. Active language gets an indigo check.

import { useState } from "react";
import { Check, Globe, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { usePrefs } from "@/contexts/PrefsContext";

export default function LanguageSwitcher({ compact = false, align = "end" }) {
  const { language, setLanguage } = usePrefs();
  const [open, setOpen] = useState(false);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === language) || SUPPORTED_LANGUAGES[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-slate-700 hover:text-indigo-700"
          data-testid="language-switcher-trigger"
        >
          <Globe className="h-4 w-4" />
          {!compact && <span className="text-sm font-medium">{current.nativeName}</span>}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-60 p-1" data-testid="language-switcher-menu">
        <div className="max-h-80 overflow-auto">
          {SUPPORTED_LANGUAGES.map((lng) => {
            const active = lng.code === language;
            return (
              <button
                key={lng.code}
                type="button"
                onClick={async () => {
                  await setLanguage(lng.code);
                  setOpen(false);
                }}
                data-testid={`language-option-${lng.code}`}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  active ? "bg-indigo-50 text-indigo-700" : "hover:bg-slate-50 text-slate-800"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="font-medium">{lng.nativeName}</span>
                  <span className="text-xs text-slate-400">{lng.code}</span>
                </span>
                {active && <Check className="h-4 w-4 text-indigo-600" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
